import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { apiService } from '../../services/apiService';

// Components
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Card from '../../components/UI/Card';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

// Icons
import { 
  Building2, 
  User, 
  Database, 
  Palette, 
  Mail, 
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Upload,
  Eye,
  EyeOff
} from 'lucide-react';
import { TakaIcon } from '../../components/UI';
import { useSettings } from '../../contexts/SettingsContext';

interface SetupData {
  company: {
    name: string;
    address: string;
    phone: string;
    email: string;
    website: string;
    logo?: File;
  };
  admin: {
    username: string;
    email: string;
    password: string;
    confirmPassword: string;
  };
  database: {
    uri: string;
  };
  currency: {
    code: string;
    symbol: string;
    name: string;
  };
  theme: {
    primaryColor: string;
    fontFamily: string;
  };
  email: {
    host: string;
    port: string;
    secure: boolean;
    user: string;
    pass: string;
  };
}

const SetupWizard: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [setupData, setSetupData] = useState<SetupData>({
    company: {
      name: 'The Aircons Ltd.',
      address: '',
      phone: '',
      email: '',
      website: ''
    },
    admin: {
      username: '',
      email: '',
      password: '',
      confirmPassword: ''
    },
    database: {
      uri: 'mongodb://localhost:27017/aircons-inventory'
    },
    currency: {
      code: 'BDT', // keep for backend, but display symbol
      symbol: '৳',
      name: 'Bangladeshi Taka'
    },
    theme: {
      primaryColor: '#2563eb',
      fontFamily: 'Times New Roman'
    },
    email: {
      host: 'smtp.gmail.com',
      port: '587',
      secure: false,
      user: '',
      pass: ''
    }
  });

  const { settings } = useSettings();

  const totalSteps = 7;

  const updateSetupData = (section: keyof SetupData, field: string, value: any) => {
    setSetupData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const validateFile = (file: File) => {
    const maxFileSize = settings?.system?.maxFileSize || 0;
    const allowedFileTypes = settings?.system?.allowedFileTypes || [];
    if (maxFileSize && file.size > maxFileSize) {
      toast.error(`File size exceeds the maximum allowed (${(maxFileSize/1024/1024).toFixed(2)} MB)`);
      return false;
    }
    if (allowedFileTypes.length > 0 && !allowedFileTypes.includes(file.type)) {
      toast.error('File type not allowed.');
      return false;
    }
    return true;
  };

  const handleFileUpload = (file: File) => {
    if (!validateFile(file)) return;
    updateSetupData('company', 'logo', file);
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1: // Company Info
        return !!(setupData.company.name && setupData.company.address && setupData.company.phone);
      case 2: // Admin User
        return !!(setupData.admin.username && setupData.admin.email && 
                 setupData.admin.password && setupData.admin.password === setupData.admin.confirmPassword &&
                 setupData.admin.password.length >= 6);
      case 3: // Database
        return !!setupData.database.uri;
      case 4: // Currency
        return !!(setupData.currency.code && setupData.currency.symbol);
      case 5: // Theme
        return !!(setupData.theme.primaryColor && setupData.theme.fontFamily);
      case 6: // Email
        return !!(setupData.email.host && setupData.email.port && setupData.email.user);
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        handleFinish();
      }
    } else {
      toast.error('Please fill in all required fields correctly.');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('setupData', JSON.stringify(setupData));
      if (setupData.company.logo) {
        formData.append('logo', setupData.company.logo);
      }

      const response = await apiService.upload('/setup/initialize', formData);

      if (response.data.success) {
        toast.success('Setup completed successfully!');
        window.location.href = '/login';
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 } as any}
            animate={{ opacity: 1, x: 0 } as any}
            exit={{ opacity: 0, x: -20 } as any}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <Building2 className="w-16 h-16 mx-auto text-blue-600 mb-4" />
              <h2 className="text-2xl font-bold text-black">Company Information</h2>
              <p className="text-black mt-2">Tell us about your company</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Company Name *"
                value={setupData.company.name}
                onChange={(e) => updateSetupData('company', 'name', e.target.value)}
                placeholder="Enter company name"
              />
              <Input
                label="Phone Number *"
                value={setupData.company.phone}
                onChange={(e) => updateSetupData('company', 'phone', e.target.value)}
                placeholder="+880-XXX-XXX-XXXX"
              />
            </div>

            <Input
              label="Company Address *"
              value={setupData.company.address}
              onChange={(e) => updateSetupData('company', 'address', e.target.value)}
              placeholder="Enter full address"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Email Address"
                type="email"
                value={setupData.company.email}
                onChange={(e) => updateSetupData('company', 'email', e.target.value)}
                placeholder="info@company.com"
              />
              <Input
                label="Website"
                value={setupData.company.website}
                onChange={(e) => updateSetupData('company', 'website', e.target.value)}
                placeholder="https://company.com"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Company Logo</label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
                  className="hidden"
                  id="logo-upload"
                />
                <label
                  htmlFor="logo-upload"
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50"
                >
                  <Upload className="w-4 h-4" />
                  <span>Upload Logo</span>
                </label>
                {setupData.company.logo && (
                  <span className="text-sm text-green-600">✓ Logo selected</span>
                )}
              </div>
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 } as any}
            animate={{ opacity: 1, x: 0 } as any}
            exit={{ opacity: 0, x: -20 } as any}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <User className="w-16 h-16 mx-auto text-blue-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900">Admin User</h2>
              <p className="text-gray-600 mt-2">Create your administrator account</p>
            </div>

            <Input
              label="Username *"
              value={setupData.admin.username}
              onChange={(e) => updateSetupData('admin', 'username', e.target.value)}
              placeholder="admin"
            />

            <Input
              label="Email Address *"
              type="email"
              value={setupData.admin.email}
              onChange={(e) => updateSetupData('admin', 'email', e.target.value)}
              placeholder="admin@company.com"
            />

            <div className="relative">
              <Input
                label="Password *"
                type={showPassword ? 'text' : 'password'}
                value={setupData.admin.password}
                onChange={(e) => updateSetupData('admin', 'password', e.target.value)}
                placeholder="Minimum 6 characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div className="relative">
              <Input
                label="Confirm Password *"
                type={showConfirmPassword ? 'text' : 'password'}
                value={setupData.admin.confirmPassword}
                onChange={(e) => updateSetupData('admin', 'confirmPassword', e.target.value)}
                placeholder="Confirm your password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {setupData.admin.password && setupData.admin.confirmPassword && 
             setupData.admin.password !== setupData.admin.confirmPassword && (
              <p className="text-red-600 text-sm">Passwords do not match</p>
            )}
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 } as any}
            animate={{ opacity: 1, x: 0 } as any}
            exit={{ opacity: 0, x: -20 } as any}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <Database className="w-16 h-16 mx-auto text-blue-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900">Database Configuration</h2>
              <p className="text-gray-600 mt-2">Configure your database connection</p>
            </div>

            <Input
              label="MongoDB URI *"
              value={setupData.database.uri}
              onChange={(e) => updateSetupData('database', 'uri', e.target.value)}
              placeholder="mongodb://localhost:27017/aircons-inventory"
            />

            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Database Requirements:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• MongoDB must be installed and running</li>
                <li>• Default connection: mongodb://localhost:27017/aircons-inventory</li>
                <li>• For production, use MongoDB Atlas or your own server</li>
              </ul>
            </div>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 } as any}
            animate={{ opacity: 1, x: 0 } as any}
            exit={{ opacity: 0, x: -20 } as any}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <TakaIcon className="w-16 h-16 mx-auto text-blue-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900">Currency Settings</h2>
              <p className="text-gray-600 mt-2">Set your default currency</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Input
                label="Currency Code *"
                value={setupData.currency.code}
                onChange={(e) => updateSetupData('currency', 'code', e.target.value.toUpperCase())}
                placeholder="৳"
              />
              <Input
                label="Currency Symbol *"
                value={setupData.currency.symbol}
                onChange={(e) => updateSetupData('currency', 'symbol', e.target.value)}
                placeholder="৳"
              />
              <Input
                label="Currency Name *"
                value={setupData.currency.name}
                onChange={(e) => updateSetupData('currency', 'name', e.target.value)}
                placeholder="Bangladeshi Taka"
              />
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-medium text-green-900 mb-2">Preview:</h4>
              <p className="text-green-800">
                {setupData.currency.symbol}1,000.00 ({setupData.currency.name})
              </p>
            </div>
          </motion.div>
        );

      case 5:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 } as any}
            animate={{ opacity: 1, x: 0 } as any}
            exit={{ opacity: 0, x: -20 } as any}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <Palette className="w-16 h-16 mx-auto text-blue-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900">Theme & Branding</h2>
              <p className="text-gray-600 mt-2">Customize your system appearance</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Color *
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={setupData.theme.primaryColor}
                    onChange={(e) => updateSetupData('theme', 'primaryColor', e.target.value)}
                    className="w-12 h-12 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    value={setupData.theme.primaryColor}
                    onChange={(e) => updateSetupData('theme', 'primaryColor', e.target.value)}
                    placeholder="#2563eb"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Font Family *
                </label>
                <select
                  value={setupData.theme.fontFamily}
                  onChange={(e) => updateSetupData('theme', 'fontFamily', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Arial">Arial</option>
                  <option value="Helvetica">Helvetica</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Verdana">Verdana</option>
                </select>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Preview:</h4>
              <div 
                className="p-4 rounded border"
                style={{ 
                  backgroundColor: setupData.theme.primaryColor,
                  fontFamily: setupData.theme.fontFamily,
                  color: 'white'
                } as unknown as React.CSSProperties}
              >
                <h3 className="text-lg font-bold">Sample Header</h3>
                <p>This is how your system will look with the selected theme.</p>
              </div>
            </div>
          </motion.div>
        );

      case 6:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 } as any}
            animate={{ opacity: 1, x: 0 } as any}
            exit={{ opacity: 0, x: -20 } as any}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <Mail className="w-16 h-16 mx-auto text-blue-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900">Email Configuration</h2>
              <p className="text-gray-600 mt-2">Set up email for notifications and invoices</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="SMTP Host *"
                value={setupData.email.host}
                onChange={(e) => updateSetupData('email', 'host', e.target.value)}
                placeholder="smtp.gmail.com"
              />
              <Input
                label="SMTP Port *"
                value={setupData.email.port}
                onChange={(e) => updateSetupData('email', 'port', e.target.value)}
                placeholder="587"
              />
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="secure"
                checked={setupData.email.secure}
                onChange={(e) => updateSetupData('email', 'secure', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="secure" className="text-sm text-gray-700">
                Use secure connection (SSL/TLS)
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Email Username *"
                value={setupData.email.user}
                onChange={(e) => updateSetupData('email', 'user', e.target.value)}
                placeholder="your-email@gmail.com"
              />
              <Input
                label="Email Password *"
                type="password"
                value={setupData.email.pass}
                onChange={(e) => updateSetupData('email', 'pass', e.target.value)}
                placeholder="App password or regular password"
              />
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <h4 className="font-medium text-yellow-900 mb-2">Email Setup Tips:</h4>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>• For Gmail, use an App Password instead of your regular password</li>
                <li>• Enable 2-factor authentication and generate an App Password</li>
                <li>• For other providers, check their SMTP settings</li>
              </ul>
            </div>
          </motion.div>
        );

      case 7:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 } as any}
            animate={{ opacity: 1, x: 0 } as any}
            exit={{ opacity: 0, x: -20 } as any}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <CheckCircle className="w-16 h-16 mx-auto text-green-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900">Review & Finish</h2>
              <p className="text-gray-600 mt-2">Review your settings before completing setup</p>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Company Information</h4>
                <p className="text-sm text-gray-700">{setupData.company.name}</p>
                <p className="text-sm text-gray-700">{setupData.company.address}</p>
                <p className="text-sm text-gray-700">{setupData.company.phone}</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Admin User</h4>
                <p className="text-sm text-gray-700">Username: {setupData.admin.username}</p>
                <p className="text-sm text-gray-700">Email: {setupData.admin.email}</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">System Settings</h4>
                <p className="text-sm text-gray-700">Currency: {setupData.currency.symbol} {setupData.currency.code}</p>
                <p className="text-sm text-gray-700">Theme: {setupData.theme.fontFamily}</p>
                <p className="text-sm text-gray-700">Database: {setupData.database.uri}</p>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Ready to Complete Setup</h4>
              <p className="text-sm text-blue-800">
                Click "Complete Setup" to initialize your Aircons Inventory Management System. 
                You'll be redirected to the login page where you can sign in with your admin credentials.
              </p>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-background)]" style={{ color: 'var(--color-text)' } as any}>
      <div className="bg-[var(--color-surface)] rounded-lg shadow-lg p-8 w-full max-w-2xl" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <h1 className="text-3xl font-bold mb-6 text-[var(--color-text)] font-['Times_New_Roman']">Setup Wizard</h1>
        <div className="mb-8">
          <div className="flex items-center space-x-4">
            {/* This section was removed as per the new_code, as the steps array is not defined. */}
            {/* {steps.map((step, idx) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-lg border-2 ${
                    currentStep === idx
                      ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
                      : idx < currentStep
                      ? 'bg-[var(--color-success)] text-white border-[var(--color-success)]'
                      : 'bg-[var(--color-surface)] text-[var(--color-text-secondary)] border-[var(--color-border)]'
                  }`}
                >
                  {idx + 1}
                </div>
                {idx < steps.length - 1 && (
                  <div className="w-8 h-1 bg-[var(--color-border)] mx-2 rounded" />
                )}
              </div>
            ))} */}
          </div>
        </div>

          {/* Step Content */}
          <AnimatePresence mode="wait">
            {renderStep()}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </Button>

            <Button
              onClick={handleNext}
              disabled={loading}
              className="flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Setting up...</span>
                </>
              ) : currentStep === totalSteps ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Complete Setup</span>
                </>
              ) : (
                <>
                  <span>Next</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
};

export default SetupWizard; 